import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, Badge } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { RefreshCw, Star, Briefcase, LogIn, Calendar, Search, AlertTriangle, CheckCircle2, BellRing, Printer, Wallet } from "lucide-react";
import { DatePicker } from "@/components/ui/date-picker";
import { CheckInDialog } from "@/components/pms/CheckInDialog";
import { AssignRoomButton } from "@/components/pms/AssignRoomButton";
import { fmtINR } from "@/lib/documents";
import { requestRegCard } from "@/components/documents/RegCardOptions";
import { useCheckinGate, GateWrap } from "@/hooks/useCheckinGate";

/** BAL-REMIND-1 · unpaid balance / fully prepaid badge for an arrival that has not checked in yet. */
function BalanceBadge({ item, compact = false }) {
  if (item.can_check_in === false || item.status === "checked_in" || item.status === "checked_out" || !item.rate_configured) return null;
  if (item.balance_due > 0) {
    return (
      <span data-testid={`arrival-balance-${item.code}`} title={item.advance_paid_total > 0 ? `Advance paid ${fmtINR(item.advance_paid_total)}` : "No advance received"}
        className="inline-flex items-center gap-1 text-[10px] mono uppercase px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/40 font-semibold">
        <Wallet className="w-2.5 h-2.5"/>Balance due {fmtINR(item.balance_due)}{!compact && item.advance_paid_total > 0 ? ` · adv ${fmtINR(item.advance_paid_total)}` : ""}
      </span>
    );
  }
  return (
    <span data-testid={`arrival-prepaid-${item.code}`} className="inline-flex items-center gap-1 text-[10px] mono uppercase px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">
      <CheckCircle2 className="w-2.5 h-2.5"/>Fully prepaid
    </span>
  );
}

function KpiCard({ label, value, active, onClick, tone = "neutral" }) {
  const tones = {
    neutral: "border-hocp-border bg-hocp-bgSubtle",
    success: "border-emerald-500/30 bg-emerald-500/5",
    warning: "border-amber-500/30 bg-amber-500/5",
    info: "border-blue-500/30 bg-blue-500/5",
    error: "border-red-500/30 bg-red-500/5",
  };
  return (
    <button
      type="button" onClick={onClick}
      data-testid={`arrival-kpi-${(label || "").toLowerCase().replace(/\s+/g, "-")}`}
      className={`text-left p-3 rounded-md border transition-all ${tones[tone]} ${active ? "ring-2 ring-white/30" : "hover:border-white/30"}`}
    >
      <div className="text-[10px] uppercase tracking-[0.15em] text-hocp-textMuted mono mb-1.5">{label}</div>
      <div className="font-mono text-xl tracking-tight text-white tabular-nums">{value ?? 0}</div>
    </button>
  );
}

const KPIS = [
  ["todays_arrivals", "Today", "neutral"],
  ["checked_in", "Checked In", "success"],
  ["pending_checkin", "Pending", "warning"],
  ["vip_arrivals", "VIP", "info"],
  ["corporate_arrivals", "Corporate", "info"],
  ["early_arrivals", "Early", "info"],
  ["late_arrivals", "Late", "warning"],
  ["walkins", "Walk-ins", "info"],
  ["rooms_ready", "Rooms Ready", "success"],
  ["rooms_not_ready", "Not Ready", "error"],
];

function ArrivalCard({ item, onCheckin, onNotifyHk, notifying, onAssigned }) {
  const gate = useCheckinGate();
  const gated = gate.isBlocked(item.arrival_date);
  const isReady = item.room_status === "available" || item.room_status === "vacant_clean";
  const isVip = item.guest_vip && item.guest_vip !== "none";
  const pending = item.can_check_in !== undefined ? item.can_check_in : item.status !== "checked_in";
  const roomNotReady = pending && item.assigned_room_id && !isReady;   // room assigned but not clean → chase HK
  const roomUnassigned = pending && !item.assigned_room_id;
  const hasBalance = pending && item.balance_due > 0;
  return (
    <Card data-testid={`arrival-card-${item.code}`} data-balance={hasBalance ? "due" : pending && item.rate_configured ? "prepaid" : "none"}
      className={`p-4 ${hasBalance ? "border-l-[3px] border-l-amber-400" : ""} ${roomNotReady ? "border-red-500/40 bg-red-500/5" : isVip ? "border-amber-500/30 bg-amber-500/5" : ""}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-1.5">
          {isVip && <Star className="w-3 h-3 text-amber-400 fill-amber-400"/>}
          {item.guest_corporate && <Briefcase className="w-3 h-3 text-blue-400"/>}
          <span className="font-mono text-[12px] text-hocp-textMuted">{item.code}</span>
        </div>
        <Badge tone={item.status === "checked_in" ? "success" : item.status === "checked_out" ? "neutral" : "warning"} data-testid={`arrival-status-${item.code}`}>{item.status === "checked_out" ? "Departed" : item.status}</Badge>
      </div>
      {(roomNotReady || roomUnassigned) && (
        <div data-testid={`arrival-hk-flag-${item.code}`}
          className="mb-2 px-2 py-1.5 rounded-md bg-red-500/10 border border-red-500/30 text-red-300">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide">
            <AlertTriangle className="w-3 h-3 shrink-0"/>
            {roomUnassigned ? "No room assigned yet" : "Room not ready · chase housekeeping"}
          </div>
          {roomUnassigned && <div className="mt-2"><AssignRoomButton reservation={item} onAssigned={onAssigned} className="w-full [&>button]:w-full" testIdPrefix="card-" /></div>}
          {roomNotReady && (
            <Button
              size="sm"
              onClick={() => onNotifyHk(item)} disabled={notifying}
              className="mt-2 h-7 w-full bg-red-600 hover:bg-red-500 text-white font-medium text-[11px] disabled:opacity-70"
              data-testid={`notify-hk-${item.code}`}
            >
              <BellRing className="w-3 h-3 mr-1.5"/>{notifying ? "Notifying…" : "Notify Housekeeping"}
            </Button>
          )}
        </div>
      )}
      <div className="text-[15px] text-white font-medium mb-1">{item.guest_name}</div>
      <div className="text-[12px] text-hocp-textMuted mono space-y-0.5">
        <div>Arrive: {(item.arrival_date || "").slice(0, 16).replace("T", " ")}</div>
        {item.estimated_arrival_time && <div>ETA: {item.estimated_arrival_time}</div>}
        <div className="flex items-center gap-1">Room: {pending && item.assigned_room_id ? <AssignRoomButton reservation={item} onAssigned={onAssigned} testIdPrefix="card-" /> : (item.room_number || "—")} <Badge tone={isReady ? "success" : "warning"} className="ml-1">{item.room_status}</Badge></div>
        <div>Source: {item.booking_source}</div>
      </div>
      <div className="mt-2 flex flex-wrap gap-1"><BalanceBadge item={item} /></div>
      {item.alerts && item.alerts.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {item.alerts.map((a, i) => (
            <span key={i} className="text-[10px] mono uppercase px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/30 flex items-center gap-1">
              <AlertTriangle className="w-2.5 h-2.5"/>{a}
            </span>
          ))}
        </div>
      )}
      {pending && item.assigned_room_id && (
        <GateWrap blocked={gated} reason={gate.reason(item.arrival_date)} className="block" testid={`checkin-gate-${item.code}`}>
          <Button size="sm" className="mt-3 w-full" onClick={() => onCheckin(item)} disabled={gated} data-testid={`checkin-${item.code}`}>
            <LogIn className="w-3 h-3 mr-1.5"/>{gated ? "Check-in from arrival date" : "Check-in"}
          </Button>
        </GateWrap>
      )}
    </Card>
  );
}

export default function ArrivalBoard() {
  const [data, setData] = useState({ items: [], kpis: {} });
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [q, setQ] = useState("");
  const [view, setView] = useState("card");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [notifyingId, setNotifyingId] = useState(null);
  const navigate = useNavigate();

  const activeHotelId = (() => {
    const m = document.cookie.split("; ").find(x => x.startsWith("active_hotel_id="));
    return m ? decodeURIComponent(m.split("=")[1]) : null;
  })();

  const printArrivalCards = () => {
    if (!activeHotelId) { toast.error("No active hotel selected"); return; }
    requestRegCard({ url: `${process.env.REACT_APP_BACKEND_URL}/api/fo/registration-cards/arrivals.pdf?hotel_id=${activeHotelId}&date=${(date || "").slice(0, 10)}`, filename: `registration-cards-${(date || "").slice(0, 10)}.pdf`, title: "Arrival Registration Cards" });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/pms/reservations/board/arrivals?date=${date}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  }, [date]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const [checkInId, setCheckInId] = useState(null);
  const gate = useCheckinGate();
  const checkin = (item) => {
    if (gate.isBlocked(item.arrival_date)) { toast.error(gate.reason(item.arrival_date)); return; }
    if (!item.assigned_room_id && !item.room_number) { toast.error("Assign a room before checking in"); navigate(`/pms/reservations/${item.id}`); return; }
    setCheckInId(item.id);
  };

  const notifyHk = async (item) => {
    setNotifyingId(item.id);
    try {
      const { data: res } = await api.post(`/fo/arrivals/${item.id}/notify-housekeeping`);
      toast.success(res.action === "prioritized"
        ? `Housekeeping bumped to URGENT for room ${res.room_number || item.room_number}`
        : `Housekeeping notified — rush clean raised for room ${res.room_number || item.room_number}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setNotifyingId(null); }
  };

  let items = data.items;
  if (statusFilter === "__balance") items = items.filter(i => i.status !== "checked_in" && i.balance_due > 0);
  else if (statusFilter) items = items.filter(i => i.status === statusFilter);
  if (q) items = items.filter(i => (i.guest_name || "").toLowerCase().includes(q.toLowerCase()) || (i.code || "").toLowerCase().includes(q.toLowerCase()));

  const pending = items.filter(i => (i.can_check_in !== undefined ? i.can_check_in : i.status !== "checked_in"));
  const arrived = items.filter(i => i.status === "checked_in" || i.status === "checked_out");

  return (
    <div className="space-y-6" data-testid="arrival-board-page">
      <PageHeader
        title="Arrival Board"
        subtitle={`${date} · ${pending.length} still to arrive · ${arrived.length} checked in`}
        action={
          <div className="flex items-center gap-2">
            <DatePicker value={(date || '').slice(0,10)} onChange={(_v) => setDate(_v)} data-testid="board-date"/>
            <Button variant="outline" onClick={printArrivalCards} data-testid="board-print-cards"><Printer className="w-4 h-4 mr-2"/>Print all arrival cards</Button>
            <Button variant="outline" onClick={load} data-testid="board-refresh"><RefreshCw className="w-4 h-4 mr-2"/>Refresh</Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="arrival-kpis">
        {KPIS.map(([k, label, tone]) => (
          <KpiCard key={k} label={label} value={data.kpis[k]} tone={tone}
            active={k === "checked_in" ? statusFilter === "checked_in" : k === "pending_checkin" ? statusFilter === "confirmed" : false}
            onClick={() => k === "checked_in" ? setStatusFilter(statusFilter === "checked_in" ? "" : "checked_in")
                       : k === "pending_checkin" ? setStatusFilter(statusFilter === "confirmed" ? "" : "confirmed") : null}/>
        ))}
      </div>

      <Card className="p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-hocp-textMuted"/>
          <Input placeholder="Search guest / code" aria-label="Search arrivals" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="arrival-search"/>
        </div>
        <Tabs value={view} onValueChange={setView}>
          <TabsList>
            <TabsTrigger value="card" data-testid="arrival-view-card">Cards</TabsTrigger>
            <TabsTrigger value="table" data-testid="arrival-view-table">Table</TabsTrigger>
          </TabsList>
        </Tabs>
        <span className="text-[11px] mono text-hocp-textMuted">{items.length} shown {loading && "· loading…"}</span>
        {data.kpis.unpaid_balance_count > 0 && (
          <button type="button" onClick={() => setStatusFilter(statusFilter === "__balance" ? "" : "__balance")} data-testid="arrival-unpaid-chip"
            className={`inline-flex items-center gap-1.5 text-[11px] mono px-2.5 py-1 rounded-full border transition-colors ${statusFilter === "__balance" ? "bg-amber-400 text-[#3B2A05] border-amber-400" : "bg-amber-500/10 text-amber-300 border-amber-500/40 hover:bg-amber-500/20"}`}>
            <Wallet className="w-3 h-3"/>{data.kpis.unpaid_balance_count} arrival{data.kpis.unpaid_balance_count === 1 ? "" : "s"} with unpaid balance · {fmtINR(data.kpis.unpaid_balance_total)}
          </button>
        )}
      </Card>

      {view === "card" ? (
        items.length ? (
          <div className="space-y-6">
            <section data-testid="arrival-section-pending">
              <div className="flex items-center gap-2 mb-3">
                <LogIn className="w-4 h-4 text-amber-400" />
                <h2 className="text-[13px] font-semibold uppercase tracking-wider text-amber-300">Still to Arrive</h2>
                <span className="text-[11px] mono px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30" data-testid="arrival-count-pending">{pending.length}</span>
              </div>
              {pending.length ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {pending.map(i => <ArrivalCard key={i.id} item={i} onCheckin={checkin} onNotifyHk={notifyHk} notifying={notifyingId === i.id} onAssigned={load}/>)}
                </div>
              ) : (
                <Card className="p-6 text-center text-hocp-textMuted text-[13px]">Everyone expected has checked in.</Card>
              )}
            </section>

            <section data-testid="arrival-section-arrived">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <h2 className="text-[13px] font-semibold uppercase tracking-wider text-emerald-300">Already Checked In</h2>
                <span className="text-[11px] mono px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" data-testid="arrival-count-arrived">{arrived.length}</span>
              </div>
              {arrived.length ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {arrived.map(i => <ArrivalCard key={i.id} item={i} onCheckin={checkin} onNotifyHk={notifyHk} notifying={notifyingId === i.id} onAssigned={load}/>)}
                </div>
              ) : (
                <Card className="p-6 text-center text-hocp-textMuted text-[13px]">No guests checked in yet</Card>
              )}
            </section>
          </div>
        ) : (
          <Card className="p-10 text-center text-hocp-textMuted text-sm" data-testid="arrival-empty">No arrivals match the filters</Card>
        )
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full" data-testid="arrival-table">
            <thead className="bg-hocp-bgCard border-b border-hocp-border">
              <tr>{["Code", "Guest", "Source", "ETA", "Room", "Room Status", "Balance", "Status", "Actions"].map(h => (
                <th key={h} className="px-4 py-2.5 text-[10px] uppercase tracking-[0.1em] text-hocp-textMuted mono text-left">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {items.map(i => {
                const isVip = i.guest_vip && i.guest_vip !== "none";
                return (
                  <tr key={i.id} className={`border-b border-hocp-border last:border-0 hover:bg-hocp-bgSubtle ${i.status !== "checked_in" && i.balance_due > 0 ? "border-l-[3px] border-l-amber-400" : ""}`}>
                    <td className="px-4 py-2 mono text-hocp-textPrimary">{i.code}</td>
                    <td className="px-4 py-2 text-hocp-textPrimary flex items-center gap-1">
                      {isVip && <Star className="w-3 h-3 text-amber-400 fill-amber-400"/>}
                      {i.guest_corporate && <Briefcase className="w-3 h-3 text-blue-400"/>}
                      {i.guest_name}
                    </td>
                    <td className="px-4 py-2 text-[12px] mono text-hocp-textMuted">{i.booking_source}</td>
                    <td className="px-4 py-2 text-[12px] mono text-hocp-textMuted">{i.estimated_arrival_time || (i.arrival_date || "").slice(11, 16)}</td>
                    <td className="px-4 py-2 mono text-hocp-textPrimary" data-testid={`arrival-room-${i.code}`}>{i.status !== "checked_in" ? <AssignRoomButton reservation={i} onAssigned={load} /> : (i.room_number || "—")}{i.status !== "checked_in" && !i.assigned_room_id && !["confirmed", "pending"].includes(i.status) ? "—" : null}</td>
                    <td className="px-4 py-2"><Badge tone={i.room_status === "available" || i.room_status === "vacant_clean" ? "success" : "warning"}>{i.room_status}</Badge></td>
                    <td className="px-4 py-2"><BalanceBadge item={i} compact /></td>
                    <td className="px-4 py-2"><Badge tone={i.status === "checked_in" ? "success" : "warning"}>{i.status}</Badge></td>
                    <td className="px-4 py-2 space-x-1">
                      <Button size="sm" variant="outline" onClick={() => navigate(`/pms/reservations/${i.id}`)}>View</Button>
                      {i.status !== "checked_in" && i.assigned_room_id && (
                        <GateWrap blocked={gate.isBlocked(i.arrival_date)} reason={gate.reason(i.arrival_date)} testid={`checkin-row-gate-${i.code}`}>
                          <Button size="sm" onClick={() => checkin(i)} disabled={gate.isBlocked(i.arrival_date)} data-testid={`checkin-row-${i.code}`}><LogIn className="w-3 h-3"/></Button>
                        </GateWrap>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!items.length && <tr><td colSpan={9} className="text-center py-10 text-hocp-textMuted text-sm">No arrivals</td></tr>}
            </tbody>
          </table>
        </Card>
      )}
      <CheckInDialog reservationId={checkInId} open={!!checkInId} onOpenChange={(v) => !v && setCheckInId(null)} onDone={() => load()} />
    </div>
  );
}
